import { Check, List } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipShortcut,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { cn } from "@/core/utils/utils";
import { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { SHEET_PREFETCH_DWELL_MS, VIEWER_OVERLAY_CLASSNAME } from "./utils";

interface SheetSwitcherProps {
  activeSheetId: string;
  isOpen: boolean;
  sheets: IAiSpreadsheetSheet[];
  onOpenChange: (isOpen: boolean) => void;
  onSheetSelect: (sheetId: string) => void;
  onSheetPrefetchIntent: (sheetId: string) => void;
}

export const SheetSwitcher = ({
  activeSheetId,
  isOpen,
  sheets,
  onOpenChange,
  onSheetSelect,
  onSheetPrefetchIntent,
}: SheetSwitcherProps) => {
  const { schedule: scheduleSheetPrefetch, cancel: cancelSheetPrefetch } =
    useDelayedAction<string>(onSheetPrefetchIntent, SHEET_PREFETCH_DWELL_MS);

  const handleSheetSelect = (sheetId: string) => {
    onSheetSelect(sheetId);
    onOpenChange(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Search sheets"
                className="relative z-10 flex w-10 shrink-0 items-center justify-center border-r border-r-stroke-tertiary bg-surface-secondary text-tertiary-text shadow-sm transition-colors hover:bg-surface-primary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <List className="h-4 w-4" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" className={VIEWER_OVERLAY_CLASSNAME}>
            <TooltipShortcut keys={["mod", "K"]}>Search sheets</TooltipShortcut>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        align="start"
        side="top"
        className={cn("w-80 p-0", VIEWER_OVERLAY_CLASSNAME)}
      >
        <Command>
          <CommandInput placeholder="Search sheets..." />
          <CommandList className="max-h-72 overflow-y-auto">
            <CommandEmpty>No sheets found.</CommandEmpty>
            <CommandGroup heading="Sheets">
              {sheets.map((sheet) => {
                const isActive = sheet.id === activeSheetId;

                return (
                  <CommandItem
                    key={sheet.id}
                    value={`${sheet.name} ${sheet.id}`}
                    onSelect={() => handleSheetSelect(sheet.id)}
                    className="min-w-0"
                    onMouseEnter={() => scheduleSheetPrefetch(sheet.id)}
                    onMouseLeave={cancelSheetPrefetch}
                    onFocus={() => scheduleSheetPrefetch(sheet.id)}
                    onBlur={cancelSheetPrefetch}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {sheet.name}
                    </span>
                    <Check
                      className={cn("text-interactive h-4 w-4", {
                        "opacity-100": isActive,
                        "opacity-0": !isActive,
                      })}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
